import { useEffect } from 'react'
import dayjs from 'dayjs'
import { Form, InputNumber, Radio } from 'antd'
import type { FormInstance } from 'antd'
import { ChevronDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { useQuery } from '@tanstack/react-query'
import { FormCard, FormActions } from '@/components/layout/FormCard'
import { fetchRobotVoices } from '@/api/robots.api'
import { fetchCategories } from '@/api/library.api'

import type { Dayjs } from 'dayjs'
import { useAtomValue } from 'jotai'
import { currentUserAtom } from '@/store/auth.atom'
import { WorkingSchedulesSection } from './working-schedules/WorkingSchedulesSection'

export const SECTION_CARD =
  'rounded-2xl ring-1 ring-[rgba(30,47,65,0.25)] bg-white p-5'
export const SECTION_TITLE = 'mb-4 text-lg font-bold text-[#1e2f41]'
export const FIELD_LABEL = 'text-base font-semibold text-[#344054]'
export const PILL_SELECT =
  '[&_.ant-select-selector]:!min-h-11 [&_.ant-select-content]:!px-4 [&_.ant-select-content]:!pt-1.5 [&_.ant-select-content]:!pb-1  [&_.ant-select-selector]:!rounded-full [&_.ant-select-selector]:!border-[rgba(30,47,65,0.25)] [&_.ant-select-selector]:!px-4 [&_.ant-select-selector]:!shadow-none [&_.ant-select-selector]:hover:!border-[#1e2f41] [&.ant-select-focused_.ant-select-selector]:!border-[#1e2f41] [&.ant-select-focused_.ant-select-selector]:!shadow-[0_0_0_3px_rgba(30,47,65,0.18)] [&_.ant-select-selection-placeholder]:!text-[#98a2b3] [&_.ant-select-selector]:!flex [&_.ant-select-selector]:!items-center [&_.ant-select-content]:!flex [&_.ant-select-content]:!items-center [&_.ant-select-content]:!h-full'
export const PILL_INPUT =
  '!h-11 !rounded-full !border-[rgba(30,47,65,0.25)] bg-white !px-4 !shadow-none !text-[#1f2937] placeholder:!text-[#98a2b3] hover:!bg-[#f7f8fa] hover:!border-[#1e2f41] focus:!bg-[#f7f8fa] focus:!border-[#1e2f41] focus:!shadow-[0_0_0_3px_rgba(30,47,65,0.18)] disabled:!bg-[#f1f3f5]'
export const FIELD_ITEM_LABEL =
  '[&_.ant-form-item-label>label]:!text-base [&_.ant-form-item-label>label]:!font-semibold [&_.ant-form-item-label>label]:!text-[#344054]'
export const TEXTAREA_INPUT =
  '!min-h-[84px] !rounded-[22px] !border-[rgba(30,47,65,0.25)] bg-[#f5f5f5] !px-4 !py-3 !text-[#1f2937] placeholder:!text-[#98a2b3] hover:!border-[#1e2f41] focus:!border-[#1e2f41] focus:!shadow-[0_0_0_3px_rgba(30,47,65,0.18)] cursor-not-allowed !resize-y'

export interface RobotFormValues {
  code?: string
  name: string
  agentName?: string
  workTime?: { start: string; end: string } | [string, string] | [Dayjs, Dayjs]
  workingSchedules?: Array<{
    id?: string
    source?: 'backend' | 'created'
    createdByAction?: boolean
    dayOfWeek?: string[]
    startTime?: Dayjs
    endTime?: Dayjs
  }>
  minEnergy: number
  energyUnit: string
  timeoutUnit: string
  timeout: number
  voice: string
  startupMethod: string
  promptDescription: string
  greeting?: string
  robotWorkingDays?: Array<{
    id?: string
    robotId?: string
    dayOfWeek: number | number[]
    dayOfWeekName?: string
    startTime: string
    endTime: string
    status?: number
  }>
  prompt60s?: string
  prompt120s?: string
  preCallPrompt?: string
  endPrompt?: string
  compliance?: string
  status?: number
  startupMode?: number
  systemPrompt?: string
  guardrails?: string
  voicePrompt?: string
  knowledgeBases?: Array<{ id: string; name?: string; createdAt?: string }>
  skillIds?: string[]
  lowBatteryThreshold?: number
  unitId?: string
  diagramId?: string
  diagramInfo?: {
    name: string
    id: string
    originFileName: string
    contentType: string
    filePath: string
    fileUrl: string
    expiresIn: number
  }
}

interface RobotFormProps {
  mode: 'create' | 'update'
  initialValues?: Partial<RobotFormValues>
  onSubmit: (values: RobotFormValues) => void
  onCancel: () => void
  isSubmitting?: boolean
  form?: FormInstance<RobotFormValues>
  hideActions?: boolean
  onValuesChange?: (
    changed: Partial<RobotFormValues>,
    all: RobotFormValues,
  ) => void
  onWorkingScheduleAdd?: () => void
  onWorkingScheduleRemove?: (row: {
    id?: string
    source?: 'backend' | 'created'
    createdByAction?: boolean
    dayOfWeek?: string[]
    startTime?: Dayjs
    endTime?: Dayjs
  }) => void
}

const { TextArea } = Input

export function RobotForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  form: externalForm,
  hideActions = false,
  onValuesChange,
  onWorkingScheduleAdd,
  onWorkingScheduleRemove,
}: RobotFormProps) {
  const [internalForm] = Form.useForm<RobotFormValues>()
  const form = externalForm ?? internalForm
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1

  const { data: voiceOptions, isLoading: isVoiceLoading } = useQuery({
    queryKey: ['robot-voices'],
    queryFn: ({ signal }) => fetchRobotVoices(signal),
    enabled: showUnitColumn,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  function normalizeWorkTime(val: any) {
    if (!val) return undefined
    if (Array.isArray(val) && val.length === 2) {
      if (typeof val[0] === 'string' && typeof val[1] === 'string') {
        const d0 = dayjs(val[0], 'HH:mm') // start
        const d1 = dayjs(val[1], 'HH:mm') // end
        if (d0.isValid() && d1.isValid()) return [d0, d1]
        return undefined
      }
      if (dayjs.isDayjs(val[0]) && dayjs.isDayjs(val[1])) {
        if (val[0].isValid() && val[1].isValid()) return val
        return undefined
      }
    }
    if (typeof val === 'object' && val.start && val.end) {
      const d0 = dayjs(val.start, 'HH:mm')
      const d1 = dayjs(val.end, 'HH:mm')
      if (d0.isValid() && d1.isValid()) return [d0, d1]
      return undefined
    }
    return undefined
  }

  function normalizeWorkingSchedules(
    val: any,
    robotWorkingDays?: RobotFormValues['robotWorkingDays'],
  ): NonNullable<RobotFormValues['workingSchedules']> {
    if (
      robotWorkingDays &&
      Array.isArray(robotWorkingDays) &&
      robotWorkingDays.length > 0
    ) {
      const grouped = new Map<
        string,
        {
          id?: string
          source: 'backend'
          createdByAction: false
          dayOfWeek: string[]
          startTime?: Dayjs
          endTime?: Dayjs
        }
      >()

      robotWorkingDays.forEach((wd) => {
        const daysArray = (
          Array.isArray(wd.dayOfWeek) ? wd.dayOfWeek : [wd.dayOfWeek]
        )
          .map((d) => String(d))
          .filter((d) => d !== '0' && d !== 'NaN')

        const key = wd.id ? `id:${wd.id}` : `time:${wd.startTime}-${wd.endTime}`

        if (!grouped.has(key)) {
          grouped.set(key, {
            id: wd.id,
            source: 'backend',
            createdByAction: false,
            dayOfWeek: [],
            startTime: dayjs(wd.startTime, 'HH:mm'),
            endTime: dayjs(wd.endTime, 'HH:mm'),
          })
        }

        const target = grouped.get(key)
        if (!target) return

        daysArray.forEach((d) => {
          if (!target.dayOfWeek.includes(d)) {
            target.dayOfWeek.push(d)
          }
        })
      })
      return Array.from(grouped.values()).map((row) => ({
        ...row,
        dayOfWeek: row.dayOfWeek.sort((a, b) => Number(a) - Number(b)),
      }))
    }

    const normalized = normalizeWorkTime(val)
    if (!normalized) {
      return [{ dayOfWeek: [], startTime: undefined, endTime: undefined }]
    }

    return [
      {
        source: 'created',
        createdByAction: false,
        dayOfWeek: ['1', '2', '3', '4', '5'],
        startTime: normalized[0],
        endTime: normalized[1],
      },
    ]
  }

  // Reset form
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        energyUnit: '%',
        timeoutUnit: 's',
        startupMethod: 'touch',
        ...initialValues,
        workTime: normalizeWorkTime(initialValues.workTime),
        workingSchedules: normalizeWorkingSchedules(
          initialValues.workTime,
          initialValues.robotWorkingDays,
        ),
      })
    }
  }, [form, initialValues])

  const renderedVoiceOptions = voiceOptions ?? []

  const normalizedInitialValues = {
    ...initialValues,
    voice: initialValues?.voice,
    workTime: normalizeWorkTime(initialValues?.workTime),
    workingSchedules: normalizeWorkingSchedules(
      initialValues?.workTime,
      initialValues?.robotWorkingDays,
    ),
  }

  function handleFinish(values: RobotFormValues) {
    const newValues = Object.fromEntries(
      Object.entries(values).map(([key, val]) => [
        key,
        typeof val === 'string' ? val.trim() : val,
      ]),
    ) as RobotFormValues

    const scheduleRows = (newValues.workingSchedules ?? []).filter(
      (row) => row.startTime && row.endTime,
    )

    if (scheduleRows.length > 0) {
      const firstRow = scheduleRows[0]
      if (
        firstRow.startTime &&
        firstRow.endTime &&
        typeof firstRow.startTime.format === 'function' &&
        typeof firstRow.endTime.format === 'function'
      ) {
        newValues.workTime = [
          firstRow.startTime.format('HH:mm'),
          firstRow.endTime.format('HH:mm'),
        ]
      }
    }

    if (
      Array.isArray(newValues.workTime) &&
      newValues.workTime[0] &&
      newValues.workTime[1] &&
      typeof newValues.workTime[0] === 'object' &&
      typeof newValues.workTime[1] === 'object' &&
      typeof (newValues.workTime[0] as any).format === 'function' &&
      typeof (newValues.workTime[1] as any).format === 'function'
    ) {
      newValues.workTime = [
        (newValues.workTime[0] as any).format('HH:mm'),
        (newValues.workTime[1] as any).format('HH:mm'),
      ]
    }

    delete (newValues as Partial<RobotFormValues>).workingSchedules

    onSubmit(newValues)
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        energyUnit: '%',
        startupMethod: 'touch',
        timeoutUnit: 's',
        ...normalizedInitialValues,
      }}
      onFinish={handleFinish}
      onValuesChange={onValuesChange}
      requiredMark={(label, info) => (
        <>
          <span className="font-medium text-gray-700">{label}</span>
          {info.required && <span className="text-red-500 ml-1">*</span>}
        </>
      )}
    >
      <FormCard
        innerClassName={`items-stretch  ${showUnitColumn ? 'md:grid-cols-2' : ''} `}
        className={`${showUnitColumn ? '' : 'border-none p-0 m-0'}`}
      >
        <div
          className={`h-full gap-4 w-full ${!showUnitColumn ? 'grid grid-cols-2' : ''}`}
        >
          <div className={SECTION_CARD}>
            <h3 className={SECTION_TITLE}>Hình thức khởi động</h3>

            <Form.Item name="startupMode" className="mb-0 w-fit">
              <Radio.Group className="flex flex-col gap-2.5 [&_.ant-radio-wrapper]:!items-center [&_.ant-radio-wrapper]:!text-base [&_.ant-radio-wrapper]:!font-medium [&_.ant-radio-wrapper]:!text-[#6b7280] [&_.ant-radio]:!top-0 [&_.ant-radio-inner]:!h-5 [&_.ant-radio-inner]:!w-5 [&_.ant-radio-inner]:!border-[2px] [&_.ant-radio-inner]:!border-[#ce7a58] [&_.ant-radio-inner::after]:!bg-[#ce7a58]">
                <Radio value={1}>Bấm nút trên màn hình</Radio>
                <Radio value={2}>Khi nhận diện khuôn mặt</Radio>
                <Radio value={3}>Kích hoạt bằng giọng nói</Radio>
              </Radio.Group>
            </Form.Item>

            <div className="mt-5 space-y-2.5">
              <h3 className={SECTION_TITLE}>Thư viện</h3>
              <span className={FIELD_LABEL}>Kiến thức</span>

              <Form.Item name="knowledgeBases" className="mb-0 ">
                <Select
                  cacheKey="robot-knowledge-bases-select"
                  lazy
                  mode="multiple"
                  maxTagCount={3}
                  placeholder="Chọn kiến thức"
                  loadOptions={async ({ page, searchText, signal }) => {
                    const response = await fetchCategories(
                      page,
                      10,
                      searchText?.trim() || undefined,
                      currentUser?.unitId || undefined,
                      signal,
                    )
                    return {
                      options: response.items.map((item: any) => ({
                        label: item.name,
                        value: item.id,
                      })),
                      hasNextPage: response.items.length === 10,
                      nextPage: page + 1,
                    }
                  }}
                  className={`${PILL_SELECT} min-h-11`}
                  onLazyValueChange={(value) => {
                    form.setFieldValue('knowledgeBases', value)
                  }}
                />
              </Form.Item>
            </div>
          </div>

          <div className={`${SECTION_CARD}`}>
            <h3 className={SECTION_TITLE}>Thời gian làm việc</h3>
            <div className="grid grid-cols-[1fr_128px] gap-3">
              <Form.Item
                name="lowBatteryThreshold"
                label="Mức năng lượng tối thiểu"
                className="mb-3"
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng nhập mức năng lượng tối thiểu',
                  },
                ]}
              >
                <InputNumber
                  min={10}
                  max={100}
                  placeholder="Nhập mức năng lượng (10-100)"
                  className={`${PILL_INPUT} w-full !h-11`}
                />
              </Form.Item>
              <Form.Item name="energyUnit" label="Đơn vị" className="mb-3">
                <Select
                  options={[{ label: '%', value: '%' }]}
                  disabled
                  className={`${PILL_SELECT} !bg-[#1E2F410D] h-11 [&_.ant-select-selector]:!bg-[#1E2F410D] [&_.ant-select-selection-item]:!font-semibold [&_.ant-select-selection-item]:!text-[#4b5563]`}
                />
              </Form.Item>
            </div>

            <div className="grid grid-cols-[1fr_128px] gap-3">
              <Form.Item
                name="timeout"
                label="Thời gian im lặng"
                className="mb-3"
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng nhập thời gian im lặng',
                  },
                ]}
              >
                <InputNumber
                  min={10}
                  max={100}
                  defaultValue={30}
                  placeholder="Nhập thời gian im lặng (10-100)"
                  className={`${PILL_INPUT} w-full !h-11`}
                />
              </Form.Item>
              <Form.Item name="timeoutUnit" label="Đơn vị" className="mb-3">
                <Select
                  options={[{ label: 'giây', value: 's' }]}
                  disabled
                  className={`${PILL_SELECT} !bg-[#1E2F410D] h-11 [&_.ant-select-selector]:!bg-[#1E2F410D] [&_.ant-select-selection-item]:!font-semibold [&_.ant-select-selection-item]:!text-[#4b5563]`}
                />
              </Form.Item>
            </div>

            <WorkingSchedulesSection
              form={form}
              onWorkingScheduleAdd={onWorkingScheduleAdd}
              onWorkingScheduleRemove={onWorkingScheduleRemove}
            />
          </div>
        </div>

        {showUnitColumn && (
          <div className="h-full ">
            <div className={SECTION_CARD}>
              <h3 className={SECTION_TITLE}>Agent</h3>
              <div className={`mb-6 ${FIELD_ITEM_LABEL}`}>
                <span className="mb-2 block">Agent sử dụng</span>
                <Form.Item name="agentName" required hidden>
                  <span />
                </Form.Item>
                <Input
                  disabled
                  value={String(
                    form.getFieldValue('agentName') ??
                      form.getFieldValue('agentName') ??
                      form.getFieldValue('name'),
                  )}
                  className={`${PILL_INPUT} !bg-[#1E2F410D]`}
                />
              </div>

              <h3 className={SECTION_TITLE}>Cấu hình robot</h3>

              <Form.Item
                name="systemPrompt"
                label="Nhiệm vụ"
                className={`mb-3 ${FIELD_ITEM_LABEL}`}
              >
                <TextArea
                  placeholder="Hiển thị mô tả prompt"
                  readOnly
                  className={`${TEXTAREA_INPUT} !resize-y`}
                />
              </Form.Item>

              <Form.Item
                name="guardrails"
                label="Tuân thủ"
                className={`mb-6 ${FIELD_ITEM_LABEL}`}
              >
                <TextArea
                  placeholder="Hiển thị yêu cầu tuân thủ"
                  readOnly
                  className={`${TEXTAREA_INPUT} !resize-y`}
                />
              </Form.Item>

              <h3 className={SECTION_TITLE}>Cấu hình giọng nói</h3>

              <Form.Item
                name="voice"
                label="Giọng nói"
                className={`mb-4 ${FIELD_ITEM_LABEL}`}
              >
                <Select
                  placeholder="Hiển thị giọng nói"
                  size="large"
                  loading={isVoiceLoading}
                  options={renderedVoiceOptions}
                  notFoundContent={
                    isVoiceLoading ? 'Đang tải...' : 'Không có dữ liệu'
                  }
                  suffixIcon={
                    <ChevronDown size={16} className="text-[#4b5563]" />
                  }
                  disabled
                  className={`${PILL_SELECT} !bg-[#1E2F410D]`}
                />
              </Form.Item>

              <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
                <Form.Item
                  name="greeting"
                  label="Lời chào mở đầu"
                  className={FIELD_ITEM_LABEL}
                >
                  <TextArea
                    placeholder="Hiển thị lời thoại"
                    readOnly
                    className={`${TEXTAREA_INPUT} !resize-y`}
                  />
                </Form.Item>

                <Form.Item
                  name="prompt60s"
                  label="Nhắc nhở tương tác sau 60s"
                  className={FIELD_ITEM_LABEL}
                >
                  <TextArea
                    placeholder="Hiển thị lời thoại"
                    readOnly
                    className={`${TEXTAREA_INPUT} !resize-y`}
                  />
                </Form.Item>

                <Form.Item
                  name="prompt120s"
                  label="Nhắc nhở tương tác sau 120s"
                  className={FIELD_ITEM_LABEL}
                >
                  <TextArea
                    placeholder="Hiển thị lời thoại"
                    readOnly
                    className={`${TEXTAREA_INPUT} !resize-y`}
                  />
                </Form.Item>

                <Form.Item
                  name="endPrompt"
                  label="Kết thúc hội thoại"
                  className={FIELD_ITEM_LABEL}
                >
                  <TextArea
                    placeholder="Hiển thị lời thoại"
                    readOnly
                    className={`${TEXTAREA_INPUT} !resize-y`}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        )}
      </FormCard>

      {!hideActions && (
        <FormActions>
          <Button
            variant="outline"
            size="md"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            htmlType="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </FormActions>
      )}
    </Form>
  )
}
